// src/hooks/useSectorEmissionsWithFood.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const SECTOR_TS_QUERY = gql`
  query SectorEmissionsTS($scenario: String) {
    emis: timeSeries(view: EMIS_GHG_BY_SET, scenario: $scenario) {
      scenario
      year
      group
      value
      commodity
    }
    food: timeSeries(view: FOOD_GHG_TOTAL, scenario: $scenario) {
      scenario
      year
      group
      value
    }
  }
`;

const EMIS_GHG_BY_SET_QUERY = gql`
  query EmisGhgVectors($scenario: String, $from: Int, $to: Int) {
    timeSeries(view: EMIS_GHG_BY_SET, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group
      value
    }
  }
`;

export function useSectorEmissionsWithFood({ scenario, from, to } = {}) {
  const { data, loading, error } = useQuery(SECTOR_TS_QUERY, {
    variables: { scenario },
    fetchPolicy: "cache-first",
  });

  // Combine series; force FOOD code for the food query
  const rows = useMemo(() => {
    const emis = data?.emis ?? [];
    const food = (data?.food ?? []).map((r) => ({ ...r, group: "FOOD" }));
    return [...emis, ...food];
  }, [data]);

  // X-axis labels
  const labels = useMemo(() => {
    const years = new Set();
    for (const r of rows) {
      if (typeof r.year !== "number") continue;
      if (from && r.year < from) continue;
      if (to && r.year > to) continue;
      years.add(r.year);
    }
    return Array.from(years).sort((a, b) => a - b).map(String);
  }, [rows, from, to]);

  // Collapse RES+SER -> BUILDINGS
  // Collapse AGR (excluding LUF*) + FOOD rows -> FOOD
  const byGroupYear = useMemo(() => {
    const map = {};
    for (const r of rows) {
      if (from && r.year < from) continue;
      if (to && r.year > to) continue;

      const rawGroup = (r.group || "OTH").toUpperCase();
      const commodity = (r.commodity || "").toUpperCase?.() || "";

      // identify LUF* whether it arrives as a group or commodity prefix
      const isLUF = rawGroup === "LUF" || rawGroup.startsWith("LUF") || commodity.startsWith("LUF");

      let g = rawGroup;

      // buildings bucket
      if (rawGroup === "RES" || rawGroup === "SER") {
        g = "BUILDINGS";
      }

      // food bucket rules:
      // - any FOOD rows (from FOOD_GHG_TOTAL) go to FOOD
      // - AGR rows only contribute to FOOD if they are NOT LUF*
      if (rawGroup === "FOOD" || (rawGroup === "AGR" && !isLUF)) {
        g = "FOOD";
      }

      // If AGR & LUF*, either drop it entirely or keep as a separate bucket:
      // Uncomment next line to DROP LUF from totals:
      // if (rawGroup === "AGR" && isLUF) continue;

      // Or keep LUF visible (won't be used by your current charts unless you add it):
      if (rawGroup === "AGR" && isLUF) {
        g = "LUF"; // optional visibility
      }

      if (!map[g]) map[g] = {};
      const v = typeof r.value === "number" ? r.value / 1000 : 0; // keep your divisor
      map[g][r.year] = (map[g][r.year] ?? 0) + v;
    }
    return map;
  }, [rows, from, to]);

  const groups = useMemo(() => Object.keys(byGroupYear).sort(), [byGroupYear]);

  return { labels, byGroupYear, groups, loading, error };
}

// Convenience: series keyed for your charts
export function useSectorEmissionsTS({ scenario, from, to } = {}) {
  const { labels, byGroupYear, loading, error } =
    useSectorEmissionsWithFood({ scenario, from, to });

  const years = useMemo(
    () =>
      (labels ?? [])
        .map((y) => (typeof y === "number" ? y : parseInt(y, 10)))
        .filter(Number.isFinite),
    [labels]
  );

  const toSeries = (code) => years.map((y) => byGroupYear?.[code]?.[y] ?? 0);

  const seriesBySector = useMemo(
    () => ({
      food: toSeries("FOOD"),        // AGR (ex-LUF) + FOOD query
      buildings: toSeries("BUILDINGS"), // RES + SER
      transport: toSeries("TRA"),
      industry: toSeries("IND"),
      // luf: toSeries("LUF"),        // optional if you kept it
    }),
    [byGroupYear, years]
  );

  return { labels: years, seriesBySector, loading, error };
}
